import React, { useState, useEffect } from 'react';
import Layout from '../../components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { ordersAPI, paymentsAPI } from '../../services/api';
import api from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Search, Eye, Package, Truck, ArrowLeft, CheckCircle, MapPin, Clock, Ban, Trash2, Loader2, Download, RefreshCw, Pencil, CreditCard, MessageCircle, Send, ShieldCheck } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../../components/ui/dialog';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../../hooks/use-toast';
import { Label } from '../../components/ui/label';
import PdfViewer from '../../components/PdfViewer';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Direct card payment form for retrying payment
const RetryPaymentQBO = ({ orderId, total, onSuccess, onError }) => {
  const [processing, setProcessing] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [card, setCard] = useState({ name: '', number: '', expMonth: '', expYear: '', cvc: '' });
  const updateCard = (field, value) => setCard(prev => ({ ...prev, [field]: value }));

  const formatCardNumber = (val) => {
    const digits = val.replace(/\D/g, '').slice(0, 16);
    return digits.replace(/(\d{4})(?=\d)/g, '$1 ');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const num = card.number.replace(/\s/g, '');
    if (!num || num.length < 13) { setErrorMsg('Please enter a valid card number'); return; }
    if (!card.expMonth || !card.expYear) { setErrorMsg('Please enter expiry date'); return; }
    if (!card.cvc || card.cvc.length < 3) { setErrorMsg('Please enter CVC'); return; }

    setProcessing(true);
    setErrorMsg('');
    try {
      const token = localStorage.getItem('at1s_token');
      const resp = await axios.post(`${API_BASE}/api/qbo/charge`, {
        order_id: orderId,
        amount: total,
        card_number: num,
        card_expiry_month: card.expMonth.padStart(2, '0'),
        card_expiry_year: card.expYear.length === 2 ? `20${card.expYear}` : card.expYear,
        card_cvc: card.cvc,
        card_name: card.name || 'Cardholder',
      }, { headers: { Authorization: `Bearer ${token}` } });

      if (resp.data.status === 'success') {
        onSuccess(orderId);
      } else {
        setErrorMsg(resp.data.message || 'Payment is processing.');
        setProcessing(false);
      }
    } catch (err) {
      const detail = err.response?.data?.detail || 'Payment failed. Please check your card details.';
      setErrorMsg(typeof detail === 'string' ? detail : JSON.stringify(detail));
      onError?.(detail);
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3" data-testid="retry-card-form">
      {errorMsg && <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-2" data-testid="retry-payment-error">{errorMsg}</div>}
      <div className="space-y-2">
        <Input value={card.name} onChange={e => updateCard('name', e.target.value)} placeholder="Cardholder Name" className="h-9" data-testid="retry-card-name" />
        <div className="relative">
          <Input value={card.number} onChange={e => updateCard('number', formatCardNumber(e.target.value))} placeholder="4111 1111 1111 1111" className="h-9 pl-9 font-mono tracking-wider" maxLength={19} data-testid="retry-card-number" />
          <CreditCard className="absolute left-2.5 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <Input value={card.expMonth} onChange={e => updateCard('expMonth', e.target.value.replace(/\D/g, '').slice(0, 2))} placeholder="MM" className="h-9 text-center font-mono" maxLength={2} data-testid="retry-card-mm" />
          <Input value={card.expYear} onChange={e => updateCard('expYear', e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="YYYY" className="h-9 text-center font-mono" maxLength={4} data-testid="retry-card-yy" />
          <Input value={card.cvc} onChange={e => updateCard('cvc', e.target.value.replace(/\D/g, '').slice(0, 4))} placeholder="CVC" className="h-9 text-center font-mono" maxLength={4} type="password" data-testid="retry-card-cvc" />
        </div>
      </div>
      <Button type="submit" disabled={processing} className="w-full bg-gradient-to-r from-green-600 to-teal-600 hover:from-green-700 hover:to-teal-700 text-white font-bold h-12 text-base rounded-xl" data-testid="retry-pay-btn">
        {processing ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Processing...</> : <><CreditCard className="h-4 w-4 mr-2" /> Pay ${total?.toFixed(2)}</>}
      </Button>
      <div className="mt-2 space-y-1.5">
        <div className="flex items-center justify-center gap-2 text-xs text-slate-400"><ShieldCheck className="h-3 w-3" /> Powered by <strong className="text-slate-500">QuickBooks Payments</strong></div>
        <div className="flex items-center justify-center gap-2 opacity-50">
          <svg viewBox="0 0 32 20" width="32" height="20"><rect width="32" height="20" rx="3" fill="#1434CB"/><text x="16" y="13" textAnchor="middle" fill="white" fontSize="7" fontWeight="bold" fontFamily="sans-serif">VISA</text></svg>
          <svg viewBox="0 0 32 20" width="32" height="20"><rect width="32" height="20" rx="3" fill="#252525"/><circle cx="13" cy="10" r="6" fill="#EB001B"/><circle cx="19" cy="10" r="6" fill="#F79E1B"/></svg>
          <svg viewBox="0 0 32 20" width="32" height="20"><rect width="32" height="20" rx="3" fill="#2E77BC"/><text x="16" y="13" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="sans-serif">AMEX</text></svg>
          <svg viewBox="0 0 32 20" width="32" height="20"><rect width="32" height="20" rx="3" fill="#000"/><text x="16" y="13" textAnchor="middle" fill="white" fontSize="6" fontWeight="bold" fontFamily="sans-serif">Pay</text></svg>
        </div>
      </div>
    </form>
  );
};


const CustomerOrders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedOrder, setSelectedOrder] = useState(null);
  const [orderDeliveries, setOrderDeliveries] = useState([]);

  // Cancel/Delete state
  const [showCancel, setShowCancel] = useState(null);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelLoading, setCancelLoading] = useState(false);
  const [showDelete, setShowDelete] = useState(null);
  const [deleteReason, setDeleteReason] = useState('');
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfOrderId, setPdfOrderId] = useState(null);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [downloadDialogOrderId, setDownloadDialogOrderId] = useState(null);

  // Payment retry state
  const [retryOrder, setRetryOrder] = useState(null);
  const [retryLoading, setRetryLoading] = useState(false);

  // Contact Us state
  const [contactOrder, setContactOrder] = useState(null);
  const [contactMessage, setContactMessage] = useState('');
  const [contactSending, setContactSending] = useState(false);

  const getInvoiceDirectUrl = (orderId) => {
    const token = localStorage.getItem('at1s_token');
    return `${process.env.REACT_APP_BACKEND_URL}/api/orders/${orderId}/invoice?token=${token}`;
  };

  const handleDownloadInvoice = (orderId) => {
    setDownloadDialogOrderId(orderId);
  };

  const handleViewInvoice = async (orderId) => {
    try {
      setInvoiceLoading(true);
      const dataUrl = await ordersAPI.viewInvoice(orderId);
      setPdfUrl(dataUrl);
      setPdfOrderId(orderId);
    } catch {
      toast({ title: 'Error', description: 'Failed to open invoice', variant: 'destructive' });
    } finally {
      setInvoiceLoading(false);
    }
  };

  const closePdfViewer = () => {
    setPdfUrl(null);
    setPdfOrderId(null);
  };

  const handleReorder = (order) => {
    const reorderItems = (order.items || []).map(item => ({
      size: (item.size || '').toUpperCase(),
      merv: String(item.merv),
      quantity: String(item.quantity),
      price: item.price,
    }));
    navigate('/customer/new-order', { state: { reorderItems } });
  };

  const handleRetryPayment = async (order) => {
    setRetryOrder(order);
    setRetryLoading(false);
  };

  const handleRetrySuccess = (orderId) => {
    toast({ title: 'Payment Successful!', description: `Order ${orderId} has been paid.` });
    setRetryOrder(null);
    fetchOrders();
  };

  const handleRetryError = (msg) => {
    toast({ title: 'Payment Failed', description: typeof msg === 'string' ? msg : 'Payment failed', variant: 'destructive' });
  };

  const openContactUs = (order) => {
    setContactOrder(order);
    setContactMessage('');
  };

  const handleSendContact = async () => {
    if (!contactOrder || !contactMessage.trim()) return;
    setContactSending(true);
    try {
      const res = await api.post('/email-logs/contact-us', {
        order_id: contactOrder.id,
        message: contactMessage.trim(),
      });
      toast({ title: 'Message Sent', description: res.data.message });
      setContactOrder(null);
      setContactMessage('');
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || 'Failed to send', variant: 'destructive' });
    } finally {
      setContactSending(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const data = await ordersAPI.getAll(user.id);
      setOrders(data.filter(o => !o.is_deleted));
    } catch (error) {
      console.error('Error fetching orders:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCancelOrder = async () => {
    if (!cancelReason.trim()) { toast({ title: 'Required', description: 'Please provide a reason', variant: 'destructive' }); return; }
    setCancelLoading(true);
    try {
      const result = await ordersAPI.customerCancelOrder(showCancel.id, cancelReason);
      const msg = result.refund_processed ? 'Order cancelled. Refund is being processed.' : 'Order cancelled successfully.';
      toast({ title: 'Cancelled', description: msg });
      setShowCancel(null);
      setCancelReason('');
      fetchOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed to cancel', variant: 'destructive' });
    } finally { setCancelLoading(false); }
  };

  const handleDeleteOrder = async () => {
    if (!deleteReason.trim()) { toast({ title: 'Required', description: 'Please provide a reason', variant: 'destructive' }); return; }
    setDeleteLoading(true);
    try {
      await ordersAPI.softDeleteOrder(showDelete.id, deleteReason);
      toast({ title: 'Removed', description: 'Order hidden from your history.' });
      setShowDelete(null);
      setDeleteReason('');
      fetchOrders();
    } catch (error) {
      toast({ title: 'Error', description: error.response?.data?.detail || 'Failed', variant: 'destructive' });
    } finally { setDeleteLoading(false); }
  };

  const getStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      shipped: 'bg-purple-100 text-purple-800',
      delivered: 'bg-green-100 text-green-800',
      cancelled: 'bg-red-100 text-red-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const getPaymentStatusColor = (status) => {
    const colors = {
      pending: 'bg-yellow-100 text-yellow-800',
      paid: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800',
      refunded: 'bg-gray-100 text-gray-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  const openOrderDetail = async (order) => {
    setSelectedOrder(order);
    try {
      const dlvs = await ordersAPI.getDeliveries(order.id);
      setOrderDeliveries(dlvs);
    } catch (e) {
      console.error('Failed to fetch deliveries:', e);
      setOrderDeliveries([]);
    }
  };

  const filteredOrders = orders.filter(order => {
    const matchesSearch = order.id.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || order.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  if (loading) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">My Orders</h1>
            <p className="text-gray-600 mt-2">Track and manage your filter orders</p>
          </div>
          <Button variant="outline" onClick={() => navigate('/customer/dashboard')}>
            <ArrowLeft className="h-4 w-4 mr-2" />Back
          </Button>
        </div>

        <Card className="border-0 shadow-lg">
          <CardContent className="pt-6">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-4 w-4" />
                <Input placeholder="Search by order ID..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-10" />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-48"><SelectValue placeholder="Filter by status" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="processing">Processing</SelectItem>
                  <SelectItem value="shipped">Shipped</SelectItem>
                  <SelectItem value="delivered">Delivered</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        <div className="space-y-4">
          {filteredOrders.length > 0 ? (
            filteredOrders.map((order) => (
              <Card key={order.id} className="border-0 shadow-lg hover:shadow-xl transition-shadow duration-200">
                <CardContent className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center space-x-3 mb-2">
                        <h3 className="text-lg font-bold text-blue-600">{order.id}</h3>
                        <Badge className={getStatusColor(order.status)}>{order.status}</Badge>
                        <Badge className={getPaymentStatusColor(order.payment_status)}>{order.payment_status}</Badge>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <p>Order Date: {new Date(order.order_date).toLocaleDateString()}</p>
                        <p>{order.items.length} item(s) • Total: ${order.total.toFixed(2)}</p>
                        {order.tracking_number && (
                          <div className="flex items-center space-x-2 text-blue-600">
                            <Truck className="h-4 w-4" />
                            <span>Tracking: {order.tracking_number}</span>
                          </div>
                        )}
                        {order.estimated_delivery && (
                          <p className="text-purple-600">Estimated Delivery: {new Date(order.estimated_delivery).toLocaleDateString()}</p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      <Button onClick={() => openOrderDetail(order)} className="bg-gradient-to-r from-blue-600 to-teal-500">
                        <Eye className="h-4 w-4 mr-2" />View Details
                      </Button>
                      {order.payment_status === 'paid' && (
                        <>
                          <Button variant="outline" onClick={() => handleViewInvoice(order.id)}
                            data-testid={`customer-view-invoice-btn-${order.id}`}>
                            <Eye className="h-4 w-4 mr-2 text-blue-600" />View
                          </Button>
                          <Button variant="outline" onClick={() => handleDownloadInvoice(order.id)}
                            data-testid={`customer-download-invoice-btn-${order.id}`}>
                            <Download className="h-4 w-4 mr-2 text-emerald-600" />Invoice
                          </Button>
                        </>
                      )}
                      <Button variant="outline" className="border-teal-200 text-teal-700 hover:bg-teal-50"
                        onClick={() => handleReorder(order)}
                        data-testid={`customer-reorder-btn-${order.id}`}>
                        <RefreshCw className="h-4 w-4 mr-1" /> Reorder
                      </Button>
                      <Button variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50"
                        onClick={() => openContactUs(order)}
                        data-testid={`customer-contact-btn-${order.id}`}>
                        <MessageCircle className="h-4 w-4 mr-1" /> Contact Us
                      </Button>
                      {order.payment_status === 'pending' && order.status !== 'cancelled' && (
                        <>
                          <Button className="bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white"
                            onClick={() => handleRetryPayment(order)}
                            data-testid={`customer-retry-pay-btn-${order.id}`}>
                            <CreditCard className="h-4 w-4 mr-1" /> Pay Now
                          </Button>
                          <Button variant="outline" className="border-sky-200 text-sky-700 hover:bg-sky-50"
                            onClick={() => navigate('/customer/new-order', { state: { editOrder: order } })}
                            data-testid={`customer-edit-btn-${order.id}`}>
                            <Pencil className="h-4 w-4 mr-1" /> Edit
                          </Button>
                        </>
                      )}
                      {(order.status === 'pending' || order.status === 'confirmed') && order.status !== 'cancelled' && (
                        <Button variant="outline" className="border-red-200 text-red-600 hover:bg-red-50"
                          onClick={() => { setShowCancel(order); setCancelReason(''); }}
                          data-testid={`customer-cancel-btn-${order.id}`}>
                          <Ban className="h-4 w-4 mr-1" /> Cancel
                        </Button>
                      )}
                      {order.status === 'cancelled' || order.status === 'delivered' ? (
                        <Button variant="ghost" size="sm" className="text-slate-400 hover:text-red-500"
                          onClick={() => { setShowDelete(order); setDeleteReason(''); }}
                          title="Remove from history"
                          data-testid={`customer-delete-btn-${order.id}`}>
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          ) : (
            <Card className="border-0 shadow-lg">
              <CardContent className="py-12">
                <div className="text-center">
                  <Package className="h-16 w-16 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500 text-lg">No orders found</p>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      </div>

      <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selectedOrder && (
            <>
              <DialogHeader>
                <DialogTitle className="text-2xl font-bold">Order Details</DialogTitle>
                <DialogDescription>Order ID: {selectedOrder.id}</DialogDescription>
              </DialogHeader>
              
              <div className="space-y-6">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500">Order Date</p>
                    <p className="font-semibold">{new Date(selectedOrder.order_date).toLocaleDateString()}</p>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500">Payment Method</p>
                    <p className="font-semibold">{selectedOrder.payment_method}</p>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Order Status</p>
                    <Badge className={getStatusColor(selectedOrder.status)}>{selectedOrder.status}</Badge>
                  </div>
                  <div>
                    <p className="text-sm text-gray-500 mb-2">Payment Status</p>
                    <Badge className={getPaymentStatusColor(selectedOrder.payment_status)}>{selectedOrder.payment_status}</Badge>
                  </div>
                </div>

                <div>
                  <p className="font-semibold mb-3">Order Items</p>
                  <div className="border rounded-lg overflow-hidden">
                    <table className="w-full">
                      <thead className="bg-gradient-to-r from-blue-50 to-teal-50">
                        <tr>
                          <th className="text-left py-2 px-4 text-sm font-semibold">Product</th>
                          <th className="text-right py-2 px-4 text-sm font-semibold">Quantity</th>
                          <th className="text-right py-2 px-4 text-sm font-semibold">Price</th>
                          <th className="text-right py-2 px-4 text-sm font-semibold">Total</th>
                        </tr>
                      </thead>
                      <tbody>
                        {selectedOrder.items.map((item, idx) => (
                          <tr key={`${item.product_id || item.size}-${item.merv}-${idx}`} className="border-t">
                            <td className="py-2 px-4"><span className="text-teal-600 font-mono text-xs mr-1">[{item.product_id || `AT1SM${item.merv}-${item.size}`}]</span> {item.size} MERV {item.merv}</td>
                            <td className="text-right py-2 px-4">{item.quantity}</td>
                            <td className="text-right py-2 px-4">${item.price}</td>
                            <td className="text-right py-2 px-4 font-semibold">${(item.quantity * item.price).toFixed(2)}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="font-semibold">${selectedOrder.subtotal.toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Shipping</span>
                    <span className="font-semibold">{selectedOrder.shipping === 0 ? 'FREE' : `$${selectedOrder.shipping.toFixed(2)}`}</span>
                  </div>
                  <div className="flex justify-between mb-2">
                    <span className="text-gray-600">Tax (8.25%)</span>
                    <span className="font-semibold">${(selectedOrder.tax || 0).toFixed(2)}</span>
                  </div>
                  <div className="flex justify-between text-lg font-bold border-t pt-2">
                    <span>Total</span>
                    <span className="text-blue-600">${selectedOrder.total.toFixed(2)}</span>
                  </div>
                  {(selectedOrder.qbo_charge_id || selectedOrder.stripe_payment_intent_id) && (
                    <div className="mt-3 bg-sky-50 border border-sky-200 rounded-lg px-3 py-2 text-xs" data-testid="customer-payment-ref">
                      <span className="font-bold text-sky-600">Payment Ref: </span>
                      <span className="font-mono text-slate-600">{selectedOrder.qbo_charge_id || selectedOrder.stripe_payment_intent_id}</span>
                    </div>
                  )}
                  {selectedOrder.payment_status === 'paid' && (
                    <div className="flex gap-2 mt-3">
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleViewInvoice(selectedOrder.id)}
                        data-testid="customer-detail-view-invoice-btn">
                        <Eye className="h-4 w-4 mr-2 text-blue-600" />View Invoice
                      </Button>
                      <Button variant="outline" size="sm" className="flex-1" onClick={() => handleDownloadInvoice(selectedOrder.id)}
                        data-testid="customer-detail-download-invoice-btn">
                        <Download className="h-4 w-4 mr-2 text-emerald-600" />Download PDF
                      </Button>
                    </div>
                  )}
                  {selectedOrder.payment_status === 'pending' && selectedOrder.status !== 'cancelled' && (
                    <div className="mt-3">
                      <Button className="w-full bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold h-11"
                        onClick={() => { setSelectedOrder(null); handleRetryPayment(selectedOrder); }}
                        data-testid="detail-retry-pay-btn">
                        <CreditCard className="h-4 w-4 mr-2" /> Pay Now — ${selectedOrder.total.toFixed(2)}
                      </Button>
                      <p className="text-xs text-slate-500 mt-1.5 text-center">Complete payment to confirm your order</p>
                    </div>
                  )}
                </div>

                <div>
                  <p className="font-semibold mb-2">Shipping Address</p>
                  <p className="text-gray-600">{selectedOrder.shipping_address}</p>
                </div>

                {/* Delivery Tracking */}
                {orderDeliveries.length > 0 && (
                  <div data-testid="delivery-tracking-section">
                    <p className="font-semibold mb-3">Delivery Tracking</p>
                    <div className="space-y-3">
                      {orderDeliveries.map((dlv, idx) => {
                        const statusSteps = ['dispatched', 'in_transit', 'delivered'];
                        const currentIdx = statusSteps.indexOf(dlv.status);
                        return (
                          <div key={dlv.id || dlv.shipment_id || idx} className="border rounded-lg p-3 bg-slate-50" data-testid={`delivery-track-${idx}`}>
                            <div className="flex justify-between items-center mb-2">
                              <span className="font-mono text-sm font-bold text-slate-700">{dlv.tracking_number}</span>
                              <Badge className={
                                dlv.status === 'delivered' ? 'bg-green-100 text-green-700' :
                                dlv.status === 'in_transit' ? 'bg-blue-100 text-blue-700' :
                                'bg-yellow-100 text-yellow-700'
                              }>{dlv.status?.replace('_', ' ')}</Badge>
                            </div>
                            {dlv.carrier_tracking_number && (
                              <p className="text-xs text-slate-500 mb-2">Carrier: <span className="font-mono">{dlv.carrier_tracking_number}</span></p>
                            )}
                            {/* Mini Timeline */}
                            <div className="flex items-center gap-1">
                              {statusSteps.map((step, sIdx) => (
                                <React.Fragment key={step}>
                                  <div className={`w-6 h-6 rounded-full flex items-center justify-center ${
                                    sIdx <= currentIdx ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                                  }`}>
                                    {sIdx <= currentIdx ? <CheckCircle className="h-3.5 w-3.5" /> :
                                      step === 'dispatched' ? <Package className="h-3 w-3" /> :
                                      step === 'in_transit' ? <Truck className="h-3 w-3" /> :
                                      <MapPin className="h-3 w-3" />}
                                  </div>
                                  {sIdx < statusSteps.length - 1 && (
                                    <div className={`h-0.5 flex-1 ${sIdx < currentIdx ? 'bg-green-400' : 'bg-gray-200'}`} />
                                  )}
                                </React.Fragment>
                              ))}
                            </div>
                            <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                              <span>Dispatched</span>
                              <span>In Transit</span>
                              <span>Delivered</span>
                            </div>
                            <div className="text-xs text-slate-400 mt-1">
                              Items: {dlv.items?.map(i => `${i.size} x${i.quantity}`).join(', ')}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}

                {selectedOrder.status !== 'delivered' && selectedOrder.status !== 'cancelled' && orderDeliveries.length === 0 && (
                  <div className="p-3 bg-yellow-50 rounded-lg flex items-center gap-2 text-sm text-yellow-700">
                    <Clock className="h-4 w-4" />
                    <span>Your order is being prepared for delivery.</span>
                  </div>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Customer Cancel Dialog */}
      <Dialog open={!!showCancel} onOpenChange={() => { if (!cancelLoading) setShowCancel(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2 text-red-700"><Ban className="h-5 w-5" /> Cancel Order</DialogTitle></DialogHeader>
          {showCancel && (
            <div className="space-y-4" data-testid="customer-cancel-dialog">
              <div className="bg-slate-50 rounded-lg p-3 text-sm">
                <p className="text-slate-500">Order: <span className="font-mono font-bold text-slate-800">{showCancel.id}</span></p>
                <p className="text-slate-500">Total: <span className="font-bold text-sky-700">${showCancel.total?.toFixed(2)}</span></p>
              </div>
              {showCancel.payment_status === 'paid' && (
                <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-800" data-testid="customer-refund-notice">
                  A full refund of <strong>${showCancel.total?.toFixed(2)}</strong> will be processed to your original payment method.
                </div>
              )}
              <div>
                <Label>Reason for cancellation <span className="text-red-500">*</span></Label>
                <textarea className="w-full border rounded-lg px-3 py-2 text-sm min-h-[70px] mt-1 focus:outline-none focus:ring-2 focus:ring-red-300"
                  placeholder="Why are you cancelling this order?" value={cancelReason}
                  onChange={e => setCancelReason(e.target.value)} data-testid="customer-cancel-reason" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowCancel(null)} disabled={cancelLoading}>Keep Order</Button>
                <Button className="flex-1 bg-red-600 hover:bg-red-700 text-white" onClick={handleCancelOrder}
                  disabled={cancelLoading || !cancelReason.trim()} data-testid="customer-confirm-cancel-btn">
                  {cancelLoading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Cancelling...</> : 'Cancel Order'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Soft Delete Dialog */}
      <Dialog open={!!showDelete} onOpenChange={() => { if (!deleteLoading) setShowDelete(null); }}>
        <DialogContent>
          <DialogHeader><DialogTitle className="flex items-center gap-2"><Trash2 className="h-5 w-5 text-slate-500" /> Remove from History</DialogTitle></DialogHeader>
          {showDelete && (
            <div className="space-y-4" data-testid="customer-delete-dialog">
              <p className="text-sm text-slate-600">This will hide order <strong>{showDelete.id}</strong> from your order history.</p>
              <div>
                <Label>Reason (optional)</Label>
                <Input className="mt-1" placeholder="Why are you removing this?" value={deleteReason}
                  onChange={e => setDeleteReason(e.target.value)} data-testid="customer-delete-reason" />
              </div>
              <div className="flex gap-3">
                <Button variant="outline" className="flex-1" onClick={() => setShowDelete(null)} disabled={deleteLoading}>Keep</Button>
                <Button className="flex-1" onClick={() => { if (!deleteReason.trim()) setDeleteReason('Customer removed from history'); handleDeleteOrder(); }}
                  disabled={deleteLoading} data-testid="customer-confirm-delete-btn">
                  {deleteLoading ? <><Loader2 className="h-4 w-4 mr-1 animate-spin" /> Removing...</> : 'Remove'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* PDF Invoice Viewer Modal */}
      <Dialog open={!!pdfUrl} onOpenChange={closePdfViewer}>
        <DialogContent className="max-w-4xl p-0 pb-2">
          <DialogHeader className="px-6 pt-4 pb-2 border-b">
            <DialogTitle className="flex items-center gap-2">
              <Eye className="h-5 w-5 text-blue-600" />
              Invoice - {pdfOrderId}
            </DialogTitle>
            <DialogDescription>Invoice preview</DialogDescription>
          </DialogHeader>
          {pdfUrl && (
            <PdfViewer
              dataUrl={pdfUrl}
              orderId={pdfOrderId}
              onDownload={() => { closePdfViewer(); handleDownloadInvoice(pdfOrderId); }}
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Download Dialog (Edge-compatible direct link) */}
      <Dialog open={!!downloadDialogOrderId} onOpenChange={() => setDownloadDialogOrderId(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Download className="h-5 w-5 text-teal-600" /> Download Invoice
            </DialogTitle>
            <DialogDescription>
              Use the link below to download or open the invoice PDF.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-teal-50 border border-teal-200 rounded-lg p-5 text-center">
              <a
                href={downloadDialogOrderId ? getInvoiceDirectUrl(downloadDialogOrderId) : '#'}
                target="_blank"
                rel="noopener noreferrer"
                className="text-lg font-bold text-teal-700 underline hover:text-teal-900 inline-flex items-center gap-2"
                data-testid="invoice-direct-download-link"
              >
                <Download className="h-5 w-5" />
                Invoice-{downloadDialogOrderId}.pdf
              </a>
              <p className="text-xs text-slate-500 mt-3">
                Click the link above to open the invoice. If blocked, <strong>right-click</strong> the link and select <strong>&ldquo;Save link as...&rdquo;</strong>
              </p>
            </div>
            <Button variant="outline" className="w-full" onClick={() => setDownloadDialogOrderId(null)}>
              Close
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Payment Retry Dialog */}
      <Dialog open={!!retryOrder} onOpenChange={(open) => { if (!open) setRetryOrder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <CreditCard className="h-5 w-5 text-teal-600" /> Complete Payment
            </DialogTitle>
            <DialogDescription>
              {retryOrder && (
                <>Order <strong>{retryOrder.id}</strong> — Total: <strong>${retryOrder.total?.toFixed(2)}</strong></>
              )}
            </DialogDescription>
          </DialogHeader>

          {retryOrder && (
            <div>
              <div className="bg-slate-50 border rounded-lg p-3 mb-4 text-sm space-y-1">
                <div className="flex justify-between">
                  <span className="text-slate-500">Items:</span>
                  <span className="font-medium">{retryOrder.items?.length || 0} item(s)</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-slate-500">Subtotal:</span>
                  <span>${retryOrder.subtotal?.toFixed(2)}</span>
                </div>
                {retryOrder.shipping > 0 && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Shipping:</span>
                    <span>${retryOrder.shipping?.toFixed(2)}</span>
                  </div>
                )}
                <div className="flex justify-between">
                  <span className="text-slate-500">Tax:</span>
                  <span>${retryOrder.tax?.toFixed(2)}</span>
                </div>
                <div className="flex justify-between font-bold text-base border-t pt-1 mt-1">
                  <span>Total:</span>
                  <span className="text-teal-700">${retryOrder.total?.toFixed(2)}</span>
                </div>
              </div>
              <RetryPaymentQBO
                orderId={retryOrder.id}
                total={retryOrder.total}
                onSuccess={handleRetrySuccess}
                onError={handleRetryError}
              />
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Contact Us Dialog */}
      <Dialog open={!!contactOrder} onOpenChange={(open) => { if (!open) setContactOrder(null); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageCircle className="h-5 w-5 text-teal-600" /> Contact Us About Order
            </DialogTitle>
            <DialogDescription>
              Your query will be sent to AirTech OneSolutions with your order details included.
            </DialogDescription>
          </DialogHeader>
          {contactOrder && (
            <div className="space-y-4">
              {/* Order Info */}
              <div className="bg-slate-50 border rounded-lg p-3 text-sm space-y-1">
                <div className="flex justify-between"><span className="text-slate-500">Order:</span><span className="font-mono font-bold">{contactOrder.id}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Date:</span><span>{new Date(contactOrder.order_date).toLocaleDateString()}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Status:</span><span>{contactOrder.status?.replace('_', ' ')}</span></div>
                <div className="flex justify-between"><span className="text-slate-500">Payment:</span><span>{contactOrder.payment_status}</span></div>
                <div className="flex justify-between font-medium"><span className="text-slate-500">Total:</span><span className="text-teal-700">${contactOrder.total?.toFixed(2)}</span></div>
                <div className="pt-1 border-t mt-1">
                  <span className="text-slate-500">Items: </span>
                  <span className="text-xs">
                    {(contactOrder.items || []).map(it => `${it.size} MERV ${it.merv} x${it.quantity}`).join(', ')}
                  </span>
                </div>
              </div>

              <div className="bg-teal-50 border border-teal-200 rounded-lg p-3 text-xs text-teal-800">
                The above order details will be automatically included in your message.
              </div>

              {/* Message */}
              <div>
                <Label className="text-sm font-medium text-slate-700">Your Message <span className="text-red-500">*</span></Label>
                <textarea
                  value={contactMessage}
                  onChange={(e) => setContactMessage(e.target.value)}
                  className="w-full mt-1.5 border rounded-lg px-3 py-2 text-sm min-h-[120px] resize-y focus:outline-none focus:ring-2 focus:ring-teal-300"
                  placeholder="Describe your query or concern about this order..."
                  data-testid="contact-message-input"
                />
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setContactOrder(null)}>Cancel</Button>
            <Button onClick={handleSendContact} disabled={contactSending || !contactMessage.trim()}
              className="bg-teal-600 hover:bg-teal-700 text-white" data-testid="send-contact-btn">
              <Send className="h-4 w-4 mr-1.5" />
              {contactSending ? 'Sending...' : 'Send Message'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
};

export default CustomerOrders;
